import React, { useState } from 'react';
import { Video, getVideoRoute, isShortVideo } from '../../lib/services/videoService';
import VideoCard from './VideoCard';

export type RowSize = 'L' | 'M' | 'S';

interface DashboardRowProps {
  /** Row display title */
  title: string;
  /** Videos to display in this row */
  videos: Video[];
  /** Row layout size: L=4 cards, M=3 cards, S=2 cards */
  size: RowSize;
  /** Unique ID for accessibility */
  headingId: string;
  /** Optional badge text for each card */
  badge?: string;
}

const GRID_CLASSES: Record<RowSize, string> = {
  L: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  M: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
  S: 'grid-cols-1 sm:grid-cols-2',
};

const SIZE_COUNTS: Record<RowSize, number> = { L: 4, M: 3, S: 2 };

function DashboardRow({ title, videos, size, headingId, badge }: DashboardRowProps) {
  const [expanded, setExpanded] = useState(false);
  const count = SIZE_COUNTS[size];
  const displayVideos = expanded ? videos : videos.slice(0, count);

  if (!videos.length) return null;

  return (
    <section className="mb-8" aria-labelledby={headingId}>
      <div className="flex items-center justify-between mb-4">
        <h2
          id={headingId}
          className="text-2xl font-bold"
          style={{ color: 'var(--vb-accent)' }}
        >
          {title}
        </h2>
        {videos.length > count && (
          <button
            onClick={() => setExpanded(prev => !prev)}
            className="text-sm transition-colors hover:underline focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded px-2 py-1"
            style={{ color: 'var(--vb-text-muted)' }}
          >
            {expanded ? 'Show less ←' : 'See all →'}
          </button>
        )}
      </div>
      <div className={`grid ${GRID_CLASSES[size]} gap-4`}>
        {displayVideos.map((video) => (
          <VideoCard
            key={video.id}
            id={video.id}
            title={video.title}
            thumbnail={video.thumbnail}
            duration={video.duration}
            aspectRatio={isShortVideo(video) ? 'short' : 'video'}
            href={getVideoRoute(video)}
            showDuration
            badge={badge}
            status={video.status}
            source={video.source}
            youtubeId={video.youtubeId}
            streams={video.streams}
            downloadUrl={video.downloadUrl}
            rawUrl={video.rawUrl}
            channelName={video.channelName}

            creatorAvatar={video.creatorAvatar}
            timeAgo={video.timeAgo}
            viewCount={video.viewCount}
          />
        ))}
      </div>
    </section>
  );
}

export default React.memo(DashboardRow);
